import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, updateDoc, doc, deleteDoc, orderBy, getDoc } from 'firebase/firestore';
import { MotionCard } from '../ui/motion-card';
import { softPlatformIcon } from './socialSoftPalette';
import { LinkedInIcon, FacebookIcon, InstagramIcon, TwitterIcon, PinterestIcon, QueueIcon, PostIcon, ClockIcon, SuccessIcon, ErrorIcon, WarningIcon, CalendarIcon, NewsFeedIcon, FolderIcon, EmptyTrayIcon, LaunchIcon, EditIcon, RetryIcon, DeleteIcon, SaveDiskIcon, TipIcon, ImageIcon, InfoIcon, CheckIcon } from '../icons';

interface QueuedPost {
  id: string;
  userId: string;
  content: string;
  platforms: string[];
  scheduledFor: Date;
  status: 'draft' | 'scheduled' | 'published' | 'failed';
  imageUrl?: string; // Legacy single image
  imageUrls?: string[]; // New: multiple images
  autoShareToNewsFeed?: boolean;
  publishedAt?: Date;
  error?: string;
  createdAt: Date;
}

interface ContentQueueProps {
  userId: string;
  onNavigateToConnections?: () => void;
}

const ContentQueue: React.FC<ContentQueueProps> = ({ userId, onNavigateToConnections }) => {
  const [posts, setPosts] = useState<QueuedPost[]>([]);
  const [filter, setFilter] = useState<'all' | 'draft' | 'scheduled' | 'published' | 'failed'>('all');
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState<QueuedPost | null>(null);
  const [connectedPlatforms, setConnectedPlatforms] = useState<string[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(true);

  const platforms = [
    { id: 'linkedin', name: 'LinkedIn', Icon: LinkedInIcon, color: softPlatformIcon('linkedin') },
    { id: 'facebook', name: 'Facebook', Icon: FacebookIcon, color: softPlatformIcon('facebook') },
    { id: 'instagram', name: 'Instagram', Icon: InstagramIcon, color: softPlatformIcon('instagram') },
    { id: 'twitter', name: 'Twitter/X', Icon: TwitterIcon, color: softPlatformIcon('twitter') },
    { id: 'pinterest', name: 'Pinterest', Icon: PinterestIcon, color: softPlatformIcon('pinterest') },
  ];

  useEffect(() => {
    loadPosts();
    loadConnections();
  }, [userId]);

  const loadConnections = async () => {
    setLoadingConnections(true);
    try {
      const docRef = doc(db, 'socialConnections', userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const connected: string[] = [];
        platforms.forEach(p => {
          if (data[p.id]?.connected) {
            connected.push(p.id);
          }
        });
        setConnectedPlatforms(connected);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
    } finally {
      setLoadingConnections(false);
    }
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      const postsQuery = query(
        collection(db, 'scheduledPosts'),
        where('userId', '==', userId),
        orderBy('scheduledFor', 'desc')
      );
      const snapshot = await getDocs(postsQuery);
      const loadedPosts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledFor: doc.data().scheduledFor.toDate(),
        createdAt: doc.data().createdAt.toDate(),
        publishedAt: doc.data().publishedAt?.toDate()
      })) as QueuedPost[];
      setPosts(loadedPosts);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Weet je zeker dat je deze post wilt verwijderen?')) return;
    
    try {
      await deleteDoc(doc(db, 'scheduledPosts', postId));
      await loadPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Fout bij verwijderen');
    }
  };

  const handlePublishNow = async (post: QueuedPost) => {
    if (!window.confirm(`Post nu direct publiceren naar ${post.platforms.join(', ')}?`)) return;

    try {
      console.log('🚀 Publishing post immediately...', post);
      
      // Import centralSharingService
      const { centralSharingService } = await import('../../services/centralSharingService');
      
      // Publish the post
      const results = await centralSharingService.shareToAllConnected(post.content, {
        platforms: post.platforms as any,
        addBranding: true,
        source: 'scheduled_queue',
        imageUrls: post.imageUrls || []
      });
      
      // Check if successful
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);
      
      if (successful.length > 0) {
        // Update post status to published
        await updateDoc(doc(db, 'scheduledPosts', post.id), {
          status: 'published',
          publishedAt: new Date(),
          publishResults: results,
          updatedAt: new Date()
        });
        
        // Also add to community feed if autoShareToNewsFeed is enabled
        console.log('🔍 Checking if should add to community feed:', {
          autoShareToNewsFeed: post.autoShareToNewsFeed,
          shouldAdd: post.autoShareToNewsFeed !== false
        });
        
        if (post.autoShareToNewsFeed !== false) {
          try {
            const { auth } = await import('../../firebase');
            const user = auth.currentUser;
            
            console.log('👤 Current user:', {
              uid: user?.uid,
              displayName: user?.displayName,
              email: user?.email
            });
            
            if (user) {
              const communityPost: any = {
                userId: user.uid,
                userName: user.displayName || user.email?.split('@')[0] || 'Gebruiker',
                userPhoto: user.photoURL || null,
                content: post.content,
                createdAt: new Date(),
                likes: 0,
                comments: 0,
                shares: 0,
                visibility: 'public',
                source: 'scheduled_post',
                scheduledPostId: post.id
              };
              
              // Add images if present
              if (post.imageUrls && post.imageUrls.length > 0) {
                communityPost.attachments = post.imageUrls.map(url => ({
                  type: 'image',
                  url: url
                }));
              }
              
              console.log('📝 Creating community post:', communityPost);
              
              const { addDoc } = await import('firebase/firestore');
              const docRef = await addDoc(collection(db, 'communityPosts'), communityPost);
              console.log('✅ Post added to community feed with ID:', docRef.id);
            } else {
              console.warn('⚠️ No user found, cannot add to community feed');
            }
          } catch (feedError) {
            console.error('❌ Failed to add post to community feed:', feedError);
            // Don't fail the whole operation if community post fails
          }
        } else {
          console.log('ℹ️ Skipping community feed (autoShareToNewsFeed is false)');
        }
        
        alert(`✅ Post gepubliceerd naar ${successful.map(r => r.platform).join(', ')}!\n\n📰 Ook in jullie community feed geplaatst!`);
        await loadPosts();
      } else {
        throw new Error(failed.map(r => `${r.platform}: ${r.error}`).join(', '));
      }
      
    } catch (error: any) {
      console.error('Error publishing post:', error);
      alert(`❌ Fout bij publiceren: ${error.message}`);
      
      // Mark as failed
      await updateDoc(doc(db, 'scheduledPosts', post.id), {
        status: 'failed',
        error: error.message,
        updatedAt: new Date()
      });
      await loadPosts();
    }
  };

  const handleUpdateStatus = async (postId: string, newStatus: QueuedPost['status']) => {
    try {
      await updateDoc(doc(db, 'scheduledPosts', postId), {
        status: newStatus,
        updatedAt: new Date()
      });
      await loadPosts();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const filteredPosts = filter === 'all' ? posts : posts.filter(p => p.status === filter);

  const statusConfig = {
    draft: { label: 'Concept', Icon: PostIcon, iconColor: 'text-gray-600', color: 'bg-gray-100 text-gray-700', border: 'border-gray-300' },
    scheduled: { label: 'Ingepland', Icon: ClockIcon, iconColor: 'text-[#4F6660]', color: 'bg-stone-100 text-[#4F6660]', border: 'border-stone-300' },
    published: { label: 'Gepubliceerd', Icon: SuccessIcon, iconColor: 'text-green-600', color: 'bg-green-100 text-green-700', border: 'border-green-300' },
    failed: { label: 'Mislukt', Icon: ErrorIcon, iconColor: 'text-[#8B7A7A]', color: 'bg-stone-100 text-[#5c4f4f]', border: 'border-stone-300' }
  };

  const getStatusCounts = () => {
    return {
      all: posts.length,
      draft: posts.filter(p => p.status === 'draft').length,
      scheduled: posts.filter(p => p.status === 'scheduled').length,
      published: posts.filter(p => p.status === 'published').length,
      failed: posts.filter(p => p.status === 'failed').length
    };
  };

  const counts = getStatusCounts();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A9488] mx-auto mb-4"></div>
          <p className="text-gray-600">Content Queue laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2"><QueueIcon size={24} weight="regular" className="text-gray-600" /> Content Queue</h2>
        <p className="text-gray-600">Beheer al je geplande en gepubliceerde content</p>
      </div>

      {/* Warning: No accounts connected */}
      {!loadingConnections && connectedPlatforms.length === 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <WarningIcon size={22} weight="regular" className="text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-medium text-amber-800">Geen social media accounts gekoppeld</h4>
              <p className="text-sm text-amber-700 mt-1">
                Je hebt nog geen accounts gekoppeld. Geplande posts zullen mislukken totdat je minimaal één account koppelt.
              </p>
              <button
                onClick={() => onNavigateToConnections?.()}
                className="mt-2 text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center gap-1 hover:underline"
              >
                <span>→</span>
                <span>Ga naar Accounts koppelen</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {(['all', 'draft', 'scheduled', 'published', 'failed'] as const).map((filterOption) => (
          <button
            key={filterOption}
            onClick={() => setFilter(filterOption)}
            className={`
              px-4 py-2 rounded-lg font-medium transition-all
              ${filter === filterOption
                ? 'bg-[#7A9488] text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}
          >
            {filterOption === 'all' ? (
              <span className="flex items-center gap-1"><FolderIcon size={16} weight="regular" /> Alle</span>
            ) : (
              <span className="flex items-center gap-1">
                {(() => { const sc = statusConfig[filterOption]; return sc ? <sc.Icon size={16} weight="regular" className={sc.iconColor} /> : null; })()}
                {statusConfig[filterOption]?.label}
              </span>
            )}
            <span className={`
              ml-2 px-2 py-0.5 text-xs rounded-full font-semibold
              ${filter === filterOption
                ? 'bg-white bg-opacity-20 text-white'
                : 'bg-gray-200 text-gray-700'
              }
            `}>
              {counts[filterOption]}
            </span>
          </button>
        ))}
      </div>

      {/* Posts List */}
      <div className="space-y-4">
        {filteredPosts.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <EmptyTrayIcon size={48} weight="regular" className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Geen posts gevonden</h3>
            <p className="text-gray-500">
              {filter === 'all' 
                ? 'Je hebt nog geen posts aangemaakt. Begin met het plannen van je eerste post!'
                : `Geen posts met status "${statusConfig[filter as keyof typeof statusConfig]?.label}"`
              }
            </p>
          </div>
        ) : (
          filteredPosts.map(post => {
            const status = statusConfig[post.status];
            return (
              <MotionCard key={post.id} className={`border-2 ${status.border} p-6`}>
                <div className="flex items-start justify-between mb-4">
                  {/* Status Badge */}
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${status.color}`}>
                    <status.Icon size={16} weight="regular" />
                    {status.label}
                  </span>

                  {/* Platforms */}
                  <div className="flex items-center space-x-2">
                    {post.platforms.map(platformId => {
                      const platform = platforms.find(p => p.id === platformId);
                      return platform ? (
                        <span key={platformId} title={platform.name}><platform.Icon size={22} weight="regular" style={{ color: platform.color }} /></span>
                      ) : null;
                    })}
                  </div>
                </div>

                {/* Content */}
                <p className="text-gray-700 mb-4 whitespace-pre-wrap">{post.content}</p>

                {/* Images Preview */}
                {(post.imageUrls && post.imageUrls.length > 0) && (
                  <div className="mb-4 flex gap-2 flex-wrap">
                    {post.imageUrls.map((url, idx) => (
                      <img 
                        key={idx} 
                        src={url} 
                        alt={`Afbeelding ${idx + 1}`}
                        className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                      />
                    ))}
                  </div>
                )}

                {/* Metadata */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-1">
                    <CalendarIcon size={14} weight="regular" className="text-gray-500" />
                    <span>Gepland: {new Date(post.scheduledFor).toLocaleString('nl-NL')}</span>
                  </div>
                  {post.publishedAt && (
                    <div className="flex items-center gap-1">
                      <SuccessIcon size={14} weight="regular" className="text-green-600" />
                      <span>Gepubliceerd: {new Date(post.publishedAt).toLocaleString('nl-NL')}</span>
                    </div>
                  )}
                  {post.autoShareToNewsFeed && (
                    <div className="flex items-center gap-1 text-[#4F6660]">
                      <NewsFeedIcon size={14} weight="regular" />
                      <span>Ook in NewsFeed</span>
                    </div>
                  )}
                </div>

                {/* Error Message - Always show helpful info for failed posts */}
                {post.status === 'failed' && (
                  <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <WarningIcon size={22} weight="regular" className="text-[#8B7A7A] flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-[#4F6660] mb-2">Publicatie mislukt</h4>
                        
                        {/* Show specific error if available */}
                        {post.error && (
                          <div className="text-sm text-[#5c4f4f] mb-3 p-2 bg-stone-100 rounded border border-stone-200">
                            <strong>Foutmelding:</strong> {post.error}
                          </div>
                        )}
                        
                        {/* Always show troubleshooting checklist */}
                        <div className="text-sm text-stone-600">
                          <p className="font-medium mb-2">Mogelijke oorzaken:</p>
                          <ul className="space-y-1 ml-4 list-disc">
                            <li>Je social media accounts zijn niet gekoppeld</li>
                            <li>Je koppeling is verlopen - connecteer opnieuw</li>
                          </ul>
                        </div>
                        
                        {/* Quick action link */}
                        <div className="mt-3 pt-3 border-t border-stone-200">
                          <button
                            onClick={() => onNavigateToConnections?.()}
                            className="text-sm text-[#4F6660] hover:text-[#3d5248] font-medium flex items-center gap-1 hover:underline"
                          >
                            <span>→</span>
                            <span>Ga naar Accounts koppelen</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {post.status === 'scheduled' && (
                    <button
                      onClick={() => handlePublishNow(post)}
                      className="px-4 py-2 bg-[#7A9488] text-white rounded-lg hover:bg-[#4F6660] text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      <LaunchIcon size={16} weight="regular" />
                      <span>Nu Publiceren</span>
                    </button>
                  )}
                  {post.status !== 'published' && (
                    <button
                      onClick={() => setEditingPost(post)}
                      className="px-4 py-2 bg-[#7A9488] text-white rounded-lg hover:bg-[#4F6660] text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      <EditIcon size={16} weight="regular" />
                      <span>Aanpassen</span>
                    </button>
                  )}
                  {post.status === 'draft' && (
                    <button
                      onClick={() => handleUpdateStatus(post.id, 'scheduled')}
                      className="px-4 py-2 bg-[#7A9488] text-white rounded-lg hover:bg-[#4F6660] text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      <ClockIcon size={16} weight="regular" /> Inplannen
                    </button>
                  )}
                  {post.status === 'failed' && (
                    <button
                      onClick={() => handleUpdateStatus(post.id, 'scheduled')}
                      className="px-4 py-2 bg-[#7A9488] text-white rounded-lg hover:bg-[#4F6660] text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      <RetryIcon size={16} weight="regular" /> Opnieuw Proberen
                    </button>
                  )}
                  <button
                    onClick={() => handleDeletePost(post.id)}
                    className="px-4 py-2 border border-stone-300 text-[#4F6660] rounded-lg hover:bg-stone-100 text-sm font-medium transition-colors ml-auto flex items-center gap-1"
                  >
                    <DeleteIcon size={16} weight="regular" /> Verwijderen
                  </button>
                </div>
              </MotionCard>
            );
          })
        )}
      </div>

      {/* Edit Modal */}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onSave={async (updatedPost) => {
            try {
              await updateDoc(doc(db, 'scheduledPosts', updatedPost.id), {
                content: updatedPost.content,
                platforms: updatedPost.platforms,
                scheduledFor: updatedPost.scheduledFor,
                updatedAt: new Date()
              });
              await loadPosts();
              setEditingPost(null);
              alert('✅ Post bijgewerkt!');
            } catch (error) {
              console.error('Error updating post:', error);
              alert('❌ Fout bij bijwerken');
            }
          }}
        />
      )}
    </div>
  );
};

// Edit Modal Component
interface EditPostModalProps {
  post: QueuedPost;
  onClose: () => void;
  onSave: (post: QueuedPost) => void;
}

const EditPostModal: React.FC<EditPostModalProps> = ({ post, onClose, onSave }) => {
  const [content, setContent] = useState(post.content);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(post.platforms);
  const [scheduledDate, setScheduledDate] = useState(
    new Date(post.scheduledFor).toISOString().split('T')[0]
  );
  const [scheduledTime, setScheduledTime] = useState(
    new Date(post.scheduledFor).toTimeString().slice(0, 5)
  );

  const platforms = [
    { id: 'linkedin', name: 'LinkedIn', Icon: LinkedInIcon, color: softPlatformIcon('linkedin') },
    { id: 'facebook', name: 'Facebook', Icon: FacebookIcon, color: softPlatformIcon('facebook') },
    { id: 'instagram', name: 'Instagram', Icon: InstagramIcon, color: softPlatformIcon('instagram') },
    { id: 'twitter', name: 'Twitter/X', Icon: TwitterIcon, color: softPlatformIcon('twitter') },
    { id: 'pinterest', name: 'Pinterest', Icon: PinterestIcon, color: softPlatformIcon('pinterest') },
  ];

  const togglePlatform = (platformId: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const handleSave = () => {
    const [hours, minutes] = scheduledTime.split(':').map(Number);
    const newDate = new Date(scheduledDate);
    newDate.setHours(hours, minutes, 0, 0);

    onSave({
      ...post,
      content,
      platforms: selectedPlatforms,
      scheduledFor: newDate
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <EditIcon size={24} weight="regular" className="text-gray-600" />
            <span>Post Aanpassen</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Content Textarea */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <PostIcon size={16} weight="regular" className="text-gray-600" /> Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488] focus:border-[#7A9488]"
              placeholder="Schrijf je post..."
            />
            <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
              <TipIcon size={12} weight="regular" className="text-gray-400" /> YouTube links worden automatisch als preview getoond op LinkedIn
            </p>
          </div>

          {/* Images Preview */}
          {post.imageUrls && post.imageUrls.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <ImageIcon size={16} weight="regular" className="text-gray-600" /> Afbeeldingen ({post.imageUrls.length})
              </label>
              <div className="flex gap-2 flex-wrap">
                {post.imageUrls.map((url, idx) => (
                  <img 
                    key={idx} 
                    src={url} 
                    alt={`Afbeelding ${idx + 1}`}
                    className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200"
                  />
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                <InfoIcon size={12} weight="regular" className="text-gray-400 flex-shrink-0" /> Afbeeldingen kunnen niet worden aangepast. Maak een nieuwe post om andere afbeeldingen te gebruiken.
              </p>
            </div>
          )}

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <CalendarIcon size={16} weight="regular" className="text-gray-600" /> Datum
              </label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <ClockIcon size={16} weight="regular" className="text-gray-600" /> Tijd
              </label>
              <input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488]"
              />
            </div>
          </div>

          {/* Platforms */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Platforms
            </label>
            <div className="grid grid-cols-2 gap-3">
              {platforms.map(platform => (
                <button
                  key={platform.id}
                  onClick={() => togglePlatform(platform.id)}
                  className={`
                    p-3 rounded-lg border-2 transition-all flex items-center gap-2
                    ${selectedPlatforms.includes(platform.id)
                      ? 'border-[#7A9488] bg-stone-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <platform.Icon size={22} weight="regular" style={{ color: platform.color }} />
                  <span className="font-medium">{platform.name}</span>
                  {selectedPlatforms.includes(platform.id) && (
                    <CheckIcon size={16} weight="bold" className="ml-auto text-[#4F6660]" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-5 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={handleSave}
            disabled={!content.trim() || selectedPlatforms.length === 0}
            className="px-6 py-2 bg-[#7A9488] text-white rounded-lg hover:bg-[#4F6660] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <SaveDiskIcon size={16} weight="regular" />
            <span>Opslaan</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentQueue;


